import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import * as XLSX from 'xlsx'

const fmt  = n => Number(n||0).toLocaleString('en-US',{maximumFractionDigits:0})
const fmtD = d => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '—'

// ── Safe replacement of ALL vouchers of a project (Legacy Issue #5) ─────────────────────────────────────────
// Real confirmation counts -> automatic backup -> ONE atomic call in the database (replace_petty_cash_vouchers).
// The old data is never deleted by the page: the database function validates, deletes and inserts inside a single
// transaction, so a failed import leaves the existing vouchers exactly as they were.
const VOUCHER_PAGE = 1000   // the API returns at most 1000 rows per request

// Reads EVERY voucher of the project (pages of 1000 in a stable order). Used for the backup.
async function fetchAllVouchers(supabase, projectId) {
  const out = []
  for (let from = 0; ; from += VOUCHER_PAGE) {
    const { data, error } = await supabase.from('petty_cash_vouchers').select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: true }).order('id', { ascending: true })
      .range(from, from + VOUCHER_PAGE - 1)
    if (error) throw error
    out.push(...(data || []))
    if (!data || data.length < VOUCHER_PAGE) break
  }
  return out
}

// Automatic backup file: every column of every existing voucher (nothing is lost). Same XLSX download pattern as the other pages.
function downloadVouchersBackup(XLSXlib, rows, projectId) {
  const ws = XLSXlib.utils.json_to_sheet(rows)
  const wb = XLSXlib.utils.book_new()
  XLSXlib.utils.book_append_sheet(wb, ws, 'petty_cash_vouchers')
  const stamp = new Date().toISOString().slice(0, 16).replace('T', '_').replace(':', '-')
  XLSXlib.writeFile(wb, `PettyCash_backup_${String(projectId).slice(0, 8)}_${stamp}.xlsx`)
}

async function replaceVouchersSafely({ supabase, projectId, rows, skipped = 0, confirmFn, backupFn }) {
  // the REAL number of existing vouchers (the list on screen may be limited to 1000 rows)
  const { count, error: countError } = await supabase.from('petty_cash_vouchers')
    .select('id', { count: 'exact', head: true }).eq('project_id', projectId)
  if (countError) throw countError
  const oldCount = count || 0

  const message = `${oldCount} existing invoices will be removed and ${rows.length} invoices will be imported.`
    + (skipped > 0 ? `\n\n${skipped} row(s) in the file were skipped (unknown Sub Code or Cost Code mismatch).` : '')
    + (oldCount > 0 ? '\n\nA backup file of the existing invoices will be downloaded automatically before the replacement.' : '')
    + '\n\nContinue?'
  if (!confirmFn(message)) return { status: 'cancelled', oldCount }

  if (oldCount > 0) {
    const existing = await fetchAllVouchers(supabase, projectId)
    if (existing.length !== oldCount) {
      throw new Error(`The backup is incomplete (${existing.length} of ${oldCount} invoices), so the import was cancelled.`)
    }
    backupFn(existing)   // if the backup fails, we stop here: nothing has been changed
  }

  const { data, error } = await supabase.rpc('replace_petty_cash_vouchers', {
    p_project_id: projectId, p_rows: rows, p_expected_old_count: oldCount,
  })
  if (error) throw error
  return { status: 'done', oldCount, ...data }
}

export default function PettyCash({ projectId }) {
  const [rows,       setRows]       = useState([])
  const [loading,    setLoading]    = useState(false)
  const [importing,  setImporting]  = useState(false)
  const [msg,        setMsg]        = useState(null)
  const [search,     setSearch]     = useState('')
  const [filterSub,  setFilterSub]  = useState('all')
  const [subCodes,   setSubCodes]   = useState([])
  const [summary,    setSummary]    = useState({})
  const [costCodes,  setCostCodes]  = useState([])
  const [subCodesList, setSubCodesList] = useState([]) // level=2 with resolved Cost Code + Resource Code ancestry
  const [showForm,   setShowForm]   = useState(false)
  const [saving,     setSaving]     = useState(false)
  const [bulkDate,   setBulkDate]   = useState('')
  const [bulkUpdating, setBulkUpdating] = useState(false)
  const [selectedRows, setSelectedRows] = useState(new Set())
  const toggleRow = (id) => setSelectedRows(prev => { const n = new Set(prev); n.has(id)?n.delete(id):n.add(id); return n })
  const toggleAll = (ids) => setSelectedRows(prev => prev.size===ids.length ? new Set() : new Set(ids))
  const emptyRow = { voucher_no:'', item_desc:'', sub_code:'', budget_code:'', cost_code_desc:'', date_of_voucher:'', vat_yn:'no', amount_exc_vat:'', amount_inc_vat:'', actual_cost_date:'2026-04-30' }
  const [form, setForm] = useState(emptyRow)

  async function applyBulkDate() {
    const ids = selectedRows.size > 0 ? [...selectedRows] : filtered.map(r=>r.id)
    if (!bulkDate) return alert('Select a date first')
    if (ids.length === 0) return alert('Select rows first')
    if (!window.confirm('Update ' + ids.length + ' records to ' + bulkDate + '?')) return
    setBulkUpdating(true)
    let hasError = false
    for (let i = 0; i < ids.length; i += 100) {
      const res = await supabase.from('petty_cash_vouchers').update({ actual_cost_date: bulkDate }).in('id', ids.slice(i, i+100))
      if (res.error) { hasError = true; setMsg({type:'error',text:'Error: '+res.error.message}); break }
    }
    if (hasError) { setBulkUpdating(false); return }
    load(); setSelectedRows(new Set()); setMsg({type:'ok',text:'Updated '+ids.length+' records'})
    setBulkUpdating(false)
  }

  const load = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    // Load cost codes for dropdowns — build full ancestry (Cost Code + Resource Code) for Sub Codes
    const { data: ccData } = await supabase
      .from('cost_codes')
      .select('id,code,description,level,parent_id')
      .eq('project_id', projectId)
      .order('code')
    const all = ccData || []
    setCostCodes(all)
    const byId = {}
    all.forEach(c => { byId[c.id] = c })
    setSubCodesList(all.filter(c => parseInt(c.level) === 2).map(sub => {
      const parent = byId[sub.parent_id]
      let cc = null, rc = null
      if (parent && parseInt(parent.level) === 4) {
        rc = parent
        cc = byId[parent.parent_id] || null
      } else if (parent && parseInt(parent.level) === 1) {
        cc = parent
      }
      return {
        ...sub,
        costCode: cc?.code || null,
        costCodeDescription: cc?.description || null,
        resourceCode: rc?.code || null,
        resourceCodeDescription: rc?.description || null,
      }
    }))
    const { data } = await supabase
      .from('petty_cash_vouchers')
      .select('*')
      .eq('project_id', projectId)
      .order('date_of_voucher', { ascending: false })
    const d = data || []
    setRows(d)
    // sub codes
    setSubCodes([...new Set(d.map(r => r.sub_code).filter(Boolean))].sort())
    // summary by sub_code
    const s = {}
    for (const r of d) {
      if (!s[r.sub_code]) s[r.sub_code] = { total: 0, count: 0 }
      s[r.sub_code].total += parseFloat(r.amount_exc_vat) || 0
      s[r.sub_code].count++
    }
    setSummary(s)
    setLoading(false)
  }, [projectId])

  useEffect(() => { load() }, [load])

  async function handleImport(e) {
    const file = e.target.files[0]
    if (!file || !projectId) return
    setImporting(true)
    setMsg(null)
    try {
      const buf  = await file.arrayBuffer()
      const wb   = XLSX.read(buf)
      const ws   = wb.Sheets[wb.SheetNames[0]]
      const raw  = XLSX.utils.sheet_to_json(ws, { header: 1 })

      // Find header row (contains "Sub code")
      let headerIdx = raw.findIndex(r => r.some(c => String(c||'').toLowerCase().includes('sub code')))
      if (headerIdx < 0) headerIdx = 3
      const headers = raw[headerIdx].map(c => String(c||'').trim().toLowerCase())

      const colIdx = {
        voucher_no:     headers.findIndex(h => h.includes('voucher') && (h.includes('no') || h.includes('claim'))),
        item_desc:      headers.findIndex(h => h.includes('item') || h.includes('desc')),
        sub_code:       headers.findIndex(h => h.includes('sub code') || h === 'sub code'),
        budget_code:    headers.findIndex(h => h.includes('budget code') || h.includes('budget')),
        cost_code_desc: headers.findIndex(h => h.includes('cost code desc')),
        date_of_voucher:headers.findIndex(h => h.includes('date')),
        vat_yn:         headers.findIndex(h => h.includes('vat')),
        amount_exc_vat: headers.findIndex(h => h.includes('exc') || (h.includes('amount') && h.includes('exc'))),
        amount_inc_vat: headers.findIndex(h => h.includes('inc') || (h.includes('amount') && h.includes('inc'))),
      }

      // If ambiguous, last amount col = inc, second to last = exc
      const amtCols = headers.map((h,i)=>h.includes('amount')?i:-1).filter(i=>i>=0)
      if (colIdx.amount_exc_vat < 0 && amtCols.length >= 2) colIdx.amount_exc_vat = amtCols[amtCols.length-2]
      if (colIdx.amount_inc_vat < 0 && amtCols.length >= 1) colIdx.amount_inc_vat = amtCols[amtCols.length-1]

      const toInsert = []
      let levelMismatch = 0, costCodeConflict = 0
      for (let i = headerIdx + 1; i < raw.length; i++) {
        const r = raw[i]
        const sub = String(r[colIdx.sub_code] || '').trim()
        if (!sub || sub.toLowerCase() === 'sub code') continue
        const amtExc = parseFloat(r[colIdx.amount_exc_vat]) || 0
        if (amtExc <= 0) continue

        const scMatch = subCodesList.find(s => s.code.trim().toLowerCase() === sub.toLowerCase())
        const rawBudgetCode = String(r[colIdx.budget_code] || '').trim()
        if (!scMatch) {
          const anyMatch = costCodes.find(c => c.code.trim().toLowerCase() === sub.toLowerCase())
          if (anyMatch) levelMismatch++
          continue // Sub Code doesn't exist in the tree — skip this row
        }
        if (rawBudgetCode && scMatch.costCode && scMatch.costCode.trim().toLowerCase() !== rawBudgetCode.toLowerCase()) {
          costCodeConflict++
          continue // written Cost Code doesn't match this Sub Code's actual parent — skip
        }

        // Date — Excel serial or real date
        let dt = null
        const rawDate = r[colIdx.date_of_voucher]
        if (rawDate instanceof Date) dt = rawDate.toISOString().slice(0,10)
        else if (typeof rawDate === 'number') {
          const d = new Date(Math.round((rawDate - 25569) * 864e5))
          dt = d.toISOString().slice(0,10)
        } else if (rawDate) dt = new Date(rawDate).toISOString().slice(0,10)

        const vatRaw = String(r[colIdx.vat_yn] || '').toLowerCase().trim()
        toInsert.push({
          project_id:      projectId,
          voucher_no:      String(r[colIdx.voucher_no] || '').trim(),
          item_desc:       String(r[colIdx.item_desc]  || '').trim(),
          sub_code:        scMatch.code,
          budget_code:     scMatch.costCode || '',
          cost_code_desc:  scMatch.costCodeDescription || '',
          date_of_voucher: dt,
          has_vat:         vatRaw === 'yes',
          amount_exc_vat:  amtExc,
          amount_inc_vat:  parseFloat(r[colIdx.amount_inc_vat]) || amtExc,
        })
      }

      if (!toInsert.length) { setMsg({type:'error',text:'No valid rows found'}); setImporting(false); return }

      // Replace ALL existing vouchers of this project: confirmation with real counts, automatic backup, atomic replacement in the database
      const outcome = await replaceVouchersSafely({
        supabase, projectId, rows: toInsert, skipped: levelMismatch + costCodeConflict,
        confirmFn: (text) => window.confirm(text),
        backupFn: (existing) => downloadVouchersBackup(XLSX, existing, projectId),
      })
      if (outcome.status === 'cancelled') {
        setMsg({ type:'error', text:'Import cancelled — no changes were made.' })
        setImporting(false)
        e.target.value = ''
        return
      }
      setMsg({ type:'success', text:`✓ Imported ${outcome.imported} vouchers (replaced ${outcome.removed}) — Total: ${fmt(outcome.total_exc_vat)} SAR (exc VAT)${(levelMismatch+costCodeConflict)>0?` — ⚠ تم تجاهل ${levelMismatch+costCodeConflict} صف (كود مش Sub Code فعلي أو Cost Code متعارض)`:''}` })
      load()
    } catch(err) {
      setMsg({ type:'error', text:'Import failed — your existing invoices were NOT changed. ' + err.message })
    }
    setImporting(false)
    e.target.value = ''
  }

  async function saveManual() {
    if (!form.sub_code || !form.amount_exc_vat) return
    setSaving(true)
    const hasVat = form.vat_yn === 'yes'
    const excVat = parseFloat(form.amount_exc_vat) || 0
    const incVat = form.amount_inc_vat ? parseFloat(form.amount_inc_vat) : hasVat ? excVat * 1.15 : excVat
    const { error } = await supabase.from('petty_cash_vouchers').insert({
      project_id:      projectId,
      voucher_no:      form.voucher_no || 'MANUAL',
      item_desc:       form.item_desc,
      sub_code:        form.sub_code,
      budget_code:     form.budget_code,
      cost_code_desc:  form.cost_code_desc,
      date_of_voucher: form.date_of_voucher || null,
      has_vat:         hasVat,
      amount_exc_vat:  excVat,
      amount_inc_vat:  incVat,
      actual_cost_date: form.actual_cost_date || '2026-04-30',
    })
    if (error) { setMsg({type:'error',text:'Save failed: '+error.message}) }
    else { setMsg({type:'success',text:'✓ Row added successfully'}); setForm(emptyRow); setShowForm(false); load() }
    setSaving(false)
  }

  const f = (k,v) => setForm(p => {
    const n = {...p, [k]:v}
    // Auto-calc inc VAT when exc changes
    if (k === 'amount_exc_vat' || k === 'vat_yn') {
      const exc = parseFloat(k==='amount_exc_vat'?v:n.amount_exc_vat) || 0
      const hasV = (k==='vat_yn'?v:n.vat_yn) === 'yes'
      n.amount_inc_vat = hasV ? (exc * 1.15).toFixed(2) : exc.toFixed(2)
    }
    return n
  })

  const filtered = rows.filter(r => {
    if (filterSub !== 'all' && r.sub_code !== filterSub) return false
    if (search && r.voucher_no !== search) return false
    return true
  })

  const totalExc = filtered.reduce((s,r) => s+(parseFloat(r.amount_exc_vat)||0), 0)
  const totalInc = filtered.reduce((s,r) => s+(parseFloat(r.amount_inc_vat)||0), 0)
  const grandTotal = rows.reduce((s,r) => s+(parseFloat(r.amount_exc_vat)||0), 0)

  return (
    <div className="min-h-screen font-sans" style={{background:'#F5F1EA'}}>

      {/* Header */}
      <div style={{background:'#F5F1EA',borderBottom:'1px solid #DDD9D0',padding:'20px 32px'}}>
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:16}}>
          <div>
            <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.14em',color:'#9A948C',marginBottom:4}}>Finance · Petty Cash</p>
            <h1 style={{fontSize:22,fontWeight:800,color:'#1C1A18'}}>Petty Cash Vouchers</h1>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={()=>setShowForm(v=>!v)}
              style={{display:'flex',alignItems:'center',gap:6,padding:'8px 14px',background:'#fff',border:'1px solid #DDD9D0',color:'#1C1A18',borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer'}}>
              + Add Row
            </button>
            <label style={{display:'flex',alignItems:'center',gap:8,padding:'8px 16px',background:'#1A7A4A',color:'#fff',borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer'}}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{width:14,height:14}}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeLinecap="round" strokeLinejoin="round"/></svg>
              {importing ? 'Importing…' : 'Import Excel'}
              <input type="file" accept=".xlsx,.xls" onChange={handleImport} style={{display:'none'}} disabled={importing}/>
            </label>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto" style={{padding:'20px 32px',display:'flex',flexDirection:'column',gap:16}}>

        {msg && (
          <div style={{padding:'10px 16px',borderRadius:8,background:msg.type==='success'?'#f0fdf4':'#fef2f2',border:`1px solid ${msg.type==='success'?'#bbf7d0':'#fecaca'}`,fontSize:12,color:msg.type==='success'?'#15803d':'#991b1b',display:'flex',justifyContent:'space-between'}}>
            <span>{msg.text}</span>
            <button onClick={()=>setMsg(null)} style={{background:'none',border:'none',cursor:'pointer',color:'inherit'}}>✕</button>
          </div>
        )}

        {/* Manual Add Form */}
        {showForm && (
          <div style={{background:'#fff',border:'1px solid #DDD9D0',borderRadius:12,padding:'20px 22px'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
              <p style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#1C1A18'}}>Add Manual Voucher Line</p>
              <button onClick={()=>{setShowForm(false);setForm(emptyRow)}} style={{background:'none',border:'none',cursor:'pointer',fontSize:16,color:'#9A948C'}}>✕</button>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:10}}>
              {/* Voucher No */}
              <div>
                <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#9A948C',marginBottom:4}}>Voucher No</p>
                <input type="text" placeholder="e.g. 232851" value={form.voucher_no}
                  onChange={e=>f('voucher_no',e.target.value)}
                  style={{width:'100%',padding:'7px 10px',border:'1px solid #DDD9D0',borderRadius:6,fontSize:12,color:'#1C1A18',outline:'none',background:'#FDFCFA'}}/>
              </div>
              {/* Item Desc */}
              <div>
                <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#9A948C',marginBottom:4}}>Item Desc</p>
                <input type="text" placeholder="Description" value={form.item_desc}
                  onChange={e=>f('item_desc',e.target.value)}
                  style={{width:'100%',padding:'7px 10px',border:'1px solid #DDD9D0',borderRadius:6,fontSize:12,color:'#1C1A18',outline:'none',background:'#FDFCFA'}}/>
              </div>
              {/* Sub Code — from cost_codes level 2; Cost Code + Resource Code are derived automatically */}
              <div>
                <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#9A948C',marginBottom:4}}>Sub Code</p>
                <select value={form.sub_code}
                  onChange={e=>{
                    const sc = subCodesList.find(s=>s.code===e.target.value)
                    setForm(p=>({...p, sub_code:e.target.value, budget_code: sc?.costCode||'', cost_code_desc: sc?.costCodeDescription||''}))
                  }}
                  style={{width:'100%',padding:'7px 10px',border:'1.5px solid #1A7A4A',borderRadius:6,fontSize:12,color:'#1C1A18',outline:'none',background:'#FDFCFA'}}>
                  <option value="">— Select Sub Code —</option>
                  {subCodesList.map(c=>(
                    <option key={c.id} value={c.code}>{c.code} — {c.description}</option>
                  ))}
                </select>
              </div>
              {/* Cost Code — derived automatically from the selected Sub Code, no longer a manual dropdown */}
              <div>
                <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#9A948C',marginBottom:4}}>Cost Code</p>
                <div style={{width:'100%',padding:'7px 10px',border:'1px solid #DDD9D0',borderRadius:6,fontSize:12,color:form.budget_code?'#1A7A4A':'#9A948C',background:'#F3F4F6',fontFamily:form.budget_code?'monospace':'inherit',fontWeight:form.budget_code?700:400}}>
                  {form.budget_code || '—'}
                </div>
                {(() => { const sc = subCodesList.find(s=>s.code===form.sub_code); return sc?.resourceCode ? (
                  <p style={{fontSize:10,color:'#7C3AED',marginTop:4}}>Resource Code: <b style={{fontFamily:'monospace'}}>{sc.resourceCode}</b>{sc.resourceCodeDescription?` — ${sc.resourceCodeDescription}`:''}</p>
                ) : null })()}
              </div>
              {/* Cost Code Desc — auto-filled */}
              <div>
                <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#9A948C',marginBottom:4}}>Cost Code Desc</p>
                <input type="text" placeholder="Auto-filled" value={form.cost_code_desc}
                  onChange={e=>f('cost_code_desc',e.target.value)}
                  style={{width:'100%',padding:'7px 10px',border:'1px solid #DDD9D0',borderRadius:6,fontSize:12,color:'#6B655C',outline:'none',background:'#F5F1EA'}}/>
              </div>
              {/* Date */}
              <div>
                <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#9A948C',marginBottom:4}}>Date</p>
                <input type="date" value={form.date_of_voucher}
                  onChange={e=>f('date_of_voucher',e.target.value)}
                  style={{width:'100%',padding:'7px 10px',border:'1px solid #DDD9D0',borderRadius:6,fontSize:12,color:'#1C1A18',outline:'none',background:'#FDFCFA'}}/>
              </div>
              <div>
                <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#9A948C',marginBottom:4}}>Actual Cost Date</p>
                <input type="date" value={form.actual_cost_date||'2026-04-30'}
                  onChange={e=>f('actual_cost_date',e.target.value)}
                  style={{width:'100%',padding:'7px 10px',border:'1px solid #DDD9D0',borderRadius:6,fontSize:12,color:'#1C1A18',outline:'none',background:'#FDFCFA'}}/>
              </div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:16}}>
              <div>
                <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#9A948C',marginBottom:4}}>VAT</p>
                <select value={form.vat_yn} onChange={e=>f('vat_yn',e.target.value)}
                  style={{width:'100%',padding:'7px 10px',border:'1px solid #DDD9D0',borderRadius:6,fontSize:12,color:'#1C1A18',outline:'none',background:'#FDFCFA'}}>
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>
              <div>
                <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#9A948C',marginBottom:4}}>Amount Exc. VAT</p>
                <input type="number" placeholder="0.00" value={form.amount_exc_vat}
                  onChange={e=>f('amount_exc_vat',e.target.value)}
                  style={{width:'100%',padding:'7px 10px',border:'1.5px solid #1A7A4A',borderRadius:6,fontSize:12,color:'#1C1A18',outline:'none',background:'#FDFCFA',fontWeight:700}}/>
              </div>
              <div>
                <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#9A948C',marginBottom:4}}>Amount Inc. VAT</p>
                <input type="number" placeholder="auto" value={form.amount_inc_vat}
                  onChange={e=>f('amount_inc_vat',e.target.value)}
                  style={{width:'100%',padding:'7px 10px',border:'1px solid #DDD9D0',borderRadius:6,fontSize:12,color:'#6B655C',outline:'none',background:'#F5F1EA'}}/>
              </div>
            </div>
            <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
              <button onClick={()=>{setShowForm(false);setForm(emptyRow)}}
                style={{padding:'8px 16px',border:'1px solid #DDD9D0',borderRadius:8,fontSize:12,background:'#fff',color:'#6B655C',cursor:'pointer'}}>Cancel</button>
              <button onClick={saveManual} disabled={saving||!form.sub_code||!form.amount_exc_vat}
                style={{padding:'8px 20px',border:'none',borderRadius:8,fontSize:12,fontWeight:700,background:form.sub_code&&form.amount_exc_vat?'#1A7A4A':'#DDD9D0',color:'#fff',cursor:form.sub_code&&form.amount_exc_vat?'pointer':'default'}}>
                {saving ? 'Saving…' : 'Save Row'}
              </button>
            </div>
          </div>
        )}

        {/* KPI Cards */}
        {rows.length > 0 && (
          <>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12}}>
              <div style={{background:'#1C1A18',borderRadius:12,padding:'18px 20px'}}>
                <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.12em',color:'rgba(255,255,255,.45)',marginBottom:8}}>Total Exc. VAT</p>
                <p style={{fontSize:22,fontWeight:800,color:'#fff'}}>{fmt(grandTotal)}</p>
                <p style={{fontSize:11,color:'rgba(255,255,255,.4)',marginTop:4}}>SAR</p>
              </div>
              <div style={{background:'#fff',border:'1px solid #DDD9D0',borderRadius:12,padding:'18px 20px'}}>
                <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.12em',color:'#9A948C',marginBottom:8}}>Total Inc. VAT</p>
                <p style={{fontSize:22,fontWeight:800,color:'#1C1A18'}}>{fmt(rows.reduce((s,r)=>s+(parseFloat(r.amount_inc_vat)||0),0))}</p>
                <p style={{fontSize:11,color:'#9A948C',marginTop:4}}>SAR</p>
              </div>
              <div style={{background:'#fff',border:'1px solid #DDD9D0',borderRadius:12,padding:'18px 20px'}}>
                <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.12em',color:'#9A948C',marginBottom:8}}>Vouchers</p>
                <p style={{fontSize:22,fontWeight:800,color:'#1C1A18'}}>{new Set(rows.map(r=>r.voucher_no)).size}</p>
                <p style={{fontSize:11,color:'#9A948C',marginTop:4}}>{rows.length} lines total</p>
              </div>
              <div style={{background:'#fff',border:'1px solid #DDD9D0',borderRadius:12,padding:'18px 20px'}}>
                <p style={{fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.12em',color:'#9A948C',marginBottom:8}}>Sub Codes</p>
                <p style={{fontSize:22,fontWeight:800,color:'#1C1A18'}}>{subCodes.length}</p>
                <p style={{fontSize:11,color:'#9A948C',marginTop:4}}>cost centres</p>
              </div>
            </div>

            {/* Summary by Sub Code */}
          </>
        )}

        {/* Filters */}
        {rows.length > 0 && (
          <div style={{display:'flex',gap:10,alignItems:'center'}}>
            <select value={search} onChange={e=>setSearch(e.target.value)}
              style={{flex:1,padding:'8px 12px',border:'1px solid #DDD9D0',borderRadius:8,fontSize:12,background:'#fff',color:'#1C1A18',outline:'none'}}>
              <option value="">All Vouchers</option>
              {[...new Set(rows.map(r=>r.voucher_no))].sort().map(v=>(
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
            <select value={filterSub} onChange={e=>setFilterSub(e.target.value)}
              style={{flex:1,padding:'8px 12px',border:'1px solid #DDD9D0',borderRadius:8,fontSize:12,background:'#fff',color:'#1C1A18',outline:'none'}}>
              <option value="all">All Sub Codes</option>
              {subCodes.map(sc=><option key={sc} value={sc}>{sc}</option>)}
            </select>
            {(search||filterSub!=='all') && (
              <button onClick={()=>{setSearch('');setFilterSub('all')}}
                style={{padding:'8px 12px',border:'1px solid #DDD9D0',borderRadius:8,fontSize:12,background:'#fff',color:'#9A948C',cursor:'pointer'}}>
                Clear
              </button>
            )}
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div style={{textAlign:'center',padding:60,color:'#9A948C',fontSize:13}}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{background:'#fff',border:'1px solid #DDD9D0',borderRadius:12,padding:60,textAlign:'center'}}>
            <p style={{fontSize:32,marginBottom:8}}>🧾</p>
            <p style={{fontSize:14,fontWeight:600,color:'#1C1A18',marginBottom:4}}>No Petty Cash Data</p>
            <p style={{fontSize:12,color:'#9A948C'}}>Import an Excel file to get started</p>
          </div>
        ) : (
          <div style={{background:'#fff',border:'1px solid #DDD9D0',borderRadius:12,overflow:'hidden'}}>
            {/* Table header */}
            <div style={{padding:'10px 16px',background:'#F5F1EA',borderBottom:'1px solid #DDD9D0',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <p style={{fontSize:11,color:'#6B655C'}}>{filtered.length} vouchers · <strong>{fmt(totalExc)}</strong> exc VAT · {fmt(totalInc)} inc VAT</p>
            </div>
            {/* Bulk update bar */}
            <div style={{display:'flex',alignItems:'center',gap:10,padding:'8px 16px',background:'#F0FDF4',borderBottom:'1px solid #BBF7D0',flexWrap:'wrap'}}>
              <span style={{fontSize:11,fontWeight:600,color:'#1A7A4A'}}>
                {selectedRows.size > 0 ? `${selectedRows.size} selected` : 'Select rows to update date'}
              </span>
              <input type="date" value={bulkDate} onChange={e=>setBulkDate(e.target.value)}
                style={{padding:'4px 8px',border:'1px solid #BBF7D0',borderRadius:4,fontSize:12,outline:'none'}}/>
              <button onClick={applyBulkDate} disabled={!bulkDate||bulkUpdating||(selectedRows.size===0)}
                style={{padding:'4px 14px',background:'#1A7A4A',color:'#fff',border:'none',borderRadius:4,fontSize:11,fontWeight:700,cursor:'pointer',opacity:(!bulkDate||bulkUpdating||selectedRows.size===0)?0.4:1}}>
                {bulkUpdating ? 'Updating...' : selectedRows.size > 0 ? `Apply to ${selectedRows.size} rows` : 'Select rows first'}
              </button>
              {selectedRows.size > 0 && <button onClick={()=>setSelectedRows(new Set())} style={{fontSize:11,color:'#6B655C',background:'none',border:'none',cursor:'pointer'}}>Clear</button>}
            </div>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead>
                  <tr style={{background:'#F5F1EA',borderBottom:'1px solid #DDD9D0'}}>
                    <th style={{width:36,padding:'8px 6px'}}><input type="checkbox" checked={selectedRows.size===filtered.slice(0,200).length&&filtered.length>0} onChange={()=>toggleAll(filtered.slice(0,200).map(r=>r.id))} style={{width:14,height:14}}/></th>
                    {['Voucher No','Item Desc','Sub Code','Cost Code','Cost Code Desc','Resource Code','Date','VAT','Exc. VAT','Inc. VAT','Actual Date'].map(h=>(
                      <th key={h} style={{padding:'8px 12px',textAlign:h==='Exc. VAT'||h==='Inc. VAT'?'right':'left',fontSize:9,fontWeight:700,textTransform:'uppercase',letterSpacing:'.1em',color:'#9A948C',whiteSpace:'nowrap'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0,200).map((r,i)=>(
                    <tr key={r.id} style={{borderBottom:'1px solid #F5F1EA',background:i%2===0?'#fff':'#FDFCFA'}}>
                      <td style={{padding:'7px 6px',textAlign:'center'}}><input type="checkbox" checked={selectedRows.has(r.id)} onChange={()=>toggleRow(r.id)} style={{width:14,height:14}}/></td>
                      <td style={{padding:'7px 12px',color:'#6B655C',fontFamily:'monospace',fontSize:11}}>{r.voucher_no}</td>
                      <td style={{padding:'7px 12px',color:'#1C1A18',maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.item_desc||'—'}</td>
                      <td style={{padding:'7px 12px'}}><span style={{background:'#EDE7DA',padding:'2px 8px',borderRadius:4,fontSize:11,fontWeight:600,color:'#1C1A18'}}>{r.sub_code}</span></td>
                      <td style={{padding:'7px 12px',fontFamily:'monospace',fontSize:11,color:'#9A948C'}}>{r.budget_code}</td>
                      <td style={{padding:'7px 12px',color:'#6B655C',maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.cost_code_desc||'—'}</td>
                      <td style={{padding:'7px 12px',fontFamily:'monospace',fontSize:11,color:'#7C3AED'}}>{subCodesList.find(s=>s.code===r.sub_code)?.resourceCode || '—'}</td>
                      <td style={{padding:'7px 12px',color:'#9A948C',whiteSpace:'nowrap'}}>{fmtD(r.date_of_voucher)}</td>
                      <td style={{padding:'7px 12px',textAlign:'center'}}>
                        <span style={{fontSize:10,fontWeight:600,color:r.has_vat?'#1A7A4A':'#9A948C'}}>{r.has_vat?'Yes':'No'}</span>
                      </td>
                      <td style={{padding:'7px 12px',textAlign:'right',fontWeight:700,color:'#1C1A18'}}>{fmt(r.amount_exc_vat)}</td>
                      <td style={{padding:'7px 12px',textAlign:'right',color:'#9A948C'}}>{fmt(r.amount_inc_vat)}</td>
                      <td style={{padding:'7px 12px',color:'#9A948C',whiteSpace:'nowrap',fontSize:11}}>{r.actual_cost_date||'—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{background:'#F5F1EA',borderTop:'2px solid #DDD9D0'}}>
                    <td colSpan={7} style={{padding:'10px 12px',fontSize:11,fontWeight:700,color:'#6B655C'}}>
                      TOTAL {search?`— Voucher ${search}`:filterSub!=='all'?`— ${filterSub}`:''}
                      <span style={{fontWeight:400,marginLeft:8,color:'#9A948C'}}>{filtered.length} lines · {new Set(filtered.map(r=>r.voucher_no)).size} vouchers</span>
                    </td>
                    <td style={{padding:'10px 12px',textAlign:'right',fontSize:13,fontWeight:800,color:'#1C1A18'}}>{fmt(totalExc)}</td>
                    <td style={{padding:'10px 12px',textAlign:'right',fontSize:12,fontWeight:600,color:'#9A948C'}}>{fmt(totalInc)}</td>
                  </tr>
                </tfoot>
              </table>
              {filtered.length > 200 && (
                <p style={{padding:'10px 16px',fontSize:11,color:'#9A948C',textAlign:'center'}}>Showing first 200 of {filtered.length} rows</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
